/**
 * Packaging OS — locale registry (Phase 1N-A).
 *
 * Source: docs/design/packaging-os/ia-decision-record.md D-5 + rtl-visual-rules.md.
 *
 * The four supported locales are:
 *   en  — English (LTR)
 *   ar  — Arabic  (RTL)
 *   fa  — Persian (RTL)
 *   tr  — Turkish (LTR)
 *
 * Direction is computed from this table. Native script labels for the
 * language switcher are pinned here so the UI never invents alt names.
 */

export type LocaleCode = "en" | "ar" | "fa" | "tr";
export type Direction = "ltr" | "rtl";

export interface Locale {
  code: LocaleCode;
  /** Native script label used in the language switcher pill. */
  nativeLabel: string;
  /** ISO/BCP-47 lang attribute for <html lang="..."> */
  lang: string;
  /** Reading direction. */
  dir: Direction;
}

export const LOCALES: readonly Locale[] = [
  { code: "en", nativeLabel: "English",   lang: "en", dir: "ltr" },
  { code: "ar", nativeLabel: "العربية",   lang: "ar", dir: "rtl" },
  { code: "fa", nativeLabel: "فارسی",     lang: "fa", dir: "rtl" },
  { code: "tr", nativeLabel: "Türkçe",    lang: "tr", dir: "ltr" },
] as const;

export const DEFAULT_LOCALE: LocaleCode = "en";

export const RTL_LOCALES: ReadonlySet<LocaleCode> = new Set(
  LOCALES.filter((l) => l.dir === "rtl").map((l) => l.code),
);

export function isRtl(code: LocaleCode): boolean {
  return RTL_LOCALES.has(code);
}

export function dirFor(code: LocaleCode): Direction {
  return isRtl(code) ? "rtl" : "ltr";
}

export function findLocale(code: string): Locale | undefined {
  return LOCALES.find((l) => l.code === code);
}
